// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { pick, set } from 'lodash-es';
import {
  LeanListing, mapToLeanListing, defaultImage, leanListingCategory, categorize,
} from '../homes/homes.helpers';

export type favouriteCanonicalStatus = 'Eligible' | 'Processing' | 'Pending' | 'Not eligible' | 'Archived' | 'Completed';
export type favouriteCanonicalStatusGroup = 'Eligible' | 'Processing' | 'Pending' | 'Not eligible' | 'Archived';

export type LeanFavourite = Partial<P.Enquiry> & {
  listing: LeanListing;
  canonicalStatus: favouriteCanonicalStatus;
  marketValue: number;
  marketValueRange: [number, number];
  url?: string;
  address?: string;
  postcode?: string;
  category?: leanListingCategory;
  status: string;
  lending_decision_metadata?: any;
  eligibleUpTo: number;
};

export function mapToCanonicalStatus(status: string): favouriteCanonicalStatus {
  switch (status) {
    // eligible property
    case 'Accepted':
    case 'AutoAccepted':
    case 'Approved':
    case 'AutoApproved':
    case 'Refer':
    case 'SecondChargeReferRequest':
    case 'SecondChargeReferred':
      return 'Eligible';
    // we are processing property
    case 'Pending':
      return 'Processing';
    // we are waiting user input
    case 'NeedInfo':
      return 'Pending';
    // not eligible property
    case 'Declined':
    case 'AutoDeclined':
    case 'Rejected':
    case 'AutoRejected':
    case 'Revoked':
      return 'Not eligible';
    // user un-favourited or expired property
    case 'Canceled':
    case 'Cancelled':
    case 'Expired':
      return 'Archived';
    // completed on property
    case 'Completed':
      return 'Completed';
    default:
      return status as favouriteCanonicalStatus;
  }
}

export function mapToLeanFavourite(
  enquiry: P.Enquiry,
  index?: number,
  enquiries?: P.Enquiry[],
  favourited?: (string | P.Listing)[],
): LeanFavourite {
  // keys used from original payload
  const keys = [
    'id',
    'url',
    'price',
    'postcode',
    'status',
    'property_type',
    'lending_decision_metadata',
    'status',
    'surface_area',
    'price',
    'createdAt',
    'updatedAt',
    'can_generate_pea',
    'pea_downloaded',
    'arranged_viewing',
    'ready_to_offer', // TODO: should be offer_made
    'progress_towards_completion',
    'listing',
    'eligibleUpTo',
  ];

  // canonical status
  const canonicalStatus = mapToCanonicalStatus(enquiry.status);

  const e = enquiry as any;

  // console.log(e.lending_decision_metadata?.category);

  // map to lean listing
  const listing: LeanListing = enquiry.listing !== null && typeof enquiry.listing === 'object'
    ? mapToLeanListing(enquiry.listing as unknown as P.Listing, undefined, undefined, favourited, enquiry)
    : {
      metadata: {
        price: e.price,
        property_type: e.property_type,
        surface_area: { sqm: e.surface_area },
        price_offset: e.lending_decision_metadata?.price_offset,
        category: e.lending_decision_metadata?.category,
      },
      source_data: {
        displayable_address: e.postcode,
        address: e.postcode,
        details_url: e.url,
        image_url: defaultImage,
        image_645_430_url: defaultImage,
      },
    } as LeanListing;

  const data = overrideFavouritedListing({
    ...enquiry,
    listing,
  } as LeanFavourite);

  const category = categorize(
    data.price,
    data.lending_decision_metadata?.price_offset,
    data.listing?.metadata?.price_change_since_listed_pct,
  );
  const marketValue = category.marketValue;
  const marketValueRange = category.marketValueRange;

  return {
    ...pick(data, keys),
    canonicalStatus,
    marketValue,
    marketValueRange,
    category: category.category,
  } as LeanFavourite;
}

export function overrideFavouritedListing(favourite: LeanFavourite): LeanFavourite {
  set(favourite, 'listing.favourite', true);

  const listing = favourite?.listing || {} as LeanListing;

  const price = favourite?.price || favourite?.lending_decision_metadata?.price || listing?.metadata?.price;
  const propertyType = favourite?.property_type || favourite?.lending_decision_metadata?.property_type || listing?.metadata?.property_type;
  const surfaceArea = favourite?.surface_area || favourite?.lending_decision_metadata?.surface_area || listing?.metadata?.surface_area?.sqm;

  const url = favourite?.url || favourite?.listing?.source_data?.details_url;

  const offset = favourite?.lending_decision_metadata?.price_offset || listing?.metadata?.price_offset;
  const forecast = favourite?.lending_decision_metadata?.ppsqm_forecast || listing?.metadata?.ppsqm_forecast;

  const address = favourite?.address || favourite?.lending_decision_metadata?.address;
  const postcode = favourite?.postcode || favourite?.lending_decision_metadata?.postcode;

  const displayableAddress = address && postcode
    ? `${address} ${postcode}`
    : listing?.source_data?.displayable_address;

  // favourite
  set(favourite, 'price', price);
  set(favourite, 'property_type', propertyType);
  set(favourite, 'surface_area', surfaceArea);
  set(favourite, 'url', url);

  // favourite.lending_decision_metadata
  set(favourite, 'lending_decision_metadata.price', price);
  set(favourite, 'lending_decision_metadata.property_type', propertyType);
  set(favourite, 'lending_decision_metadata.surface_area', surfaceArea);

  set(favourite, 'lending_decision_metadata.price_offset', offset);
  set(favourite, 'lending_decision_metadata.ppsqm_forecast', forecast);

  // listing.metadata
  set(listing, 'metadata.price', price);
  set(listing, 'metadata.property_type', propertyType);
  set(listing, 'metadata.surface_area', surfaceArea);

  set(listing, 'metadata.price_offset', offset);
  set(listing, 'metadata.ppsqm_forecast', forecast);

  set(listing, 'source_data.displayable_address', displayableAddress);

  const category = categorize(price, offset, listing?.metadata?.price_change_since_listed_pct);
  const marketValue = category.marketValue;
  const marketValueRange = category.marketValueRange;

  set(listing, 'metadata.category', category.category);
  set(listing, 'metadata.marketValue', marketValue);
  set(listing, 'metadata.marketValueRange', marketValueRange);

  return favourite;
}

export function favouriteGroup(canonicalStatus: favouriteCanonicalStatus): favouriteCanonicalStatusGroup {
  let status = canonicalStatus;

  switch (canonicalStatus) {
    case 'Eligible':
    case 'Completed':
      status = 'Eligible';
      break;
    // return 'Eligible';
    case 'Not eligible':
      status = 'Not eligible';
      break;
    case 'Archived':
      status = 'Archived';
      break;
    // return 'Not eligible';
    default:
      status = 'Pending';
      break;
    // return canonicalStatus;
  }

  return status;
}
